import { useState, useEffect, useRef } from "react";

const gold = "#f5a623", blue = "#3b82f6", blue2 = "#60a5fa";
const green = "#22c55e", red = "#ef4444", purple = "#a78bfa";
const navy = "#0b0f1a", navy2 = "#111827", navy3 = "#1a2236", navy4 = "#212d45";
const white = "#ffffff", textCol = "#cbd5e1", muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}
function Reveal({ children, delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "none" : "translateY(22px)", transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const ss = {
  section: { marginBottom: 72 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  line: { flex: 1, height: 1, background: border },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 900, color: white, lineHeight: 1.25, marginBottom: 20 },
  prose: { fontSize: "0.97rem", color: textCol, lineHeight: 1.85, marginBottom: 28 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "48px 0" },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "28px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function Pill({ text, color = gold }) {
  return <span style={{ display: "inline-block", fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color, background: `${color}18`, border: `1px solid ${color}33`, marginRight: 4, marginBottom: 4 }}>{text}</span>;
}

// ── S1: Equity Fund Types ──
function EquityFundTabs() {
  const [active, setActive] = useState(0);
  const funds = [
    {
      icon: "🏛️", label: "Standard Equity", color: blue2, badge: "Income + Growth",
      objective: "Earn a combination of dividend income and capital gains.",
      holdings: "Canadian common stocks, focused on large-capitalization 'Blue Chip' firms with strong dividend records.",
      risk: "Higher than fixed-income but lower than growth funds. Capital is at risk.",
      mgmt: "Active management. Managers pick quality dividend-paying companies.",
      audience: "Conservative equity investors who want some income alongside growth.",
      pills: ["Blue Chip", "Dividend Income", "Large Cap", "Active Mgmt"],
    },
    {
      icon: "🚀", label: "Equity Growth", color: gold, badge: "Capital Gains Only",
      objective: "Maximize capital gains. Dividend income is NOT a priority.",
      holdings: "Smaller, fast-growing companies that reinvest earnings rather than paying dividends.",
      risk: "Higher — small firms fail more often. Often trade at high P/E ratios making prices volatile.",
      mgmt: "Active management. Managers seek undervalued growth companies.",
      audience: "Long-horizon investors comfortable with high volatility.",
      pills: ["High P/E", "No Dividends", "Higher Failure Rate", "Small / Mid Cap"],
    },
    {
      icon: "📊", label: "Equity Index", color: green, badge: "Passive Management",
      objective: "Replicate the performance of a market index (e.g., S&P/TSX Composite).",
      holdings: "Stocks in the same proportion as the index. Some funds use derivatives + T-bills instead of physical stocks.",
      risk: "Market risk only — no manager risk. Tracks the index up AND down.",
      mgmt: "Passive — no active research or security selection. Results in lower MER.",
      audience: "Cost-conscious investors who accept market returns.",
      pills: ["Passive Mgmt", "Low MER", "Tracks Index", "No Alpha Attempt"],
    },
    {
      icon: "🌱", label: "Small Cap", color: purple, badge: "Aggressive Growth",
      objective: "Capital gains through investment in smaller, lesser-known firms.",
      holdings: "Small capitalization companies — often early-stage or niche businesses.",
      risk: "Highest equity risk. Small firms are more prone to failure and illiquidity. High volatility.",
      mgmt: "Active — requires deep research into lesser-known companies.",
      audience: "Aggressive investors with long time horizons and high risk tolerance.",
      pills: ["Highest Volatility", "Illiquidity Risk", "Small Cap", "Aggressive"],
    },
    {
      icon: "🌍", label: "Responsible (ESG)", color: "#4ade80", badge: "ESG Factors",
      objective: "Earn returns while incorporating Environmental, Social & Governance criteria.",
      holdings: "Companies that meet ESG screening — avoids heavy polluters, poor labour practices, weak governance.",
      risk: "Similar to standard equity funds, but potentially reduced long-term ESG risks.",
      mgmt: "Active screening process. Companies with poor ESG scores are excluded.",
      audience: "Values-aligned investors seeking risk reduction through ESG screening.",
      pills: ["ESG Screened", "Responsible Investing", "Long-Term Risk Reduction", "Values-Aligned"],
    },
  ];
  const f = funds[active];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 0 }}>
        {funds.map((fn, i) => (
          <button key={i} onClick={() => setActive(i)} style={{ padding: "9px 14px", background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? fn.color + "55" : border}`, borderBottom: active === i ? `2px solid ${fn.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.75rem", fontWeight: 700, color: active === i ? fn.color : muted, transition: "all 0.2s", whiteSpace: "nowrap" }}>
            {fn.icon} {fn.label}
          </button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${f.color}22`, borderRadius: "0 0 8px 8px", padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <span style={{ fontSize: "1.6rem" }}>{f.icon}</span>
          <div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem", color: white }}>{f.label} Fund</div>
            <span style={{ fontSize: "0.65rem", fontWeight: 700, padding: "2px 10px", borderRadius: 100, color: f.color, background: `${f.color}18`, border: `1px solid ${f.color}33` }}>{f.badge}</span>
          </div>
        </div>
        <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
          {[["Objective", f.objective, green], ["Holdings", f.holdings, blue2], ["Risk", f.risk, red], ["Management", f.mgmt, gold], ["Best For", f.audience, f.color]].map(([lbl, val, col]) => (
            <div key={lbl} style={{ background: navy3, borderRadius: 6, padding: "10px 14px", display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, width: 100, flexShrink: 0, paddingTop: 2 }}>{lbl}</div>
              <div style={{ fontSize: "0.84rem", color: textCol, flex: 1, lineHeight: 1.6 }}>{val}</div>
            </div>
          ))}
        </div>
        <div>{f.pills.map((p, i) => <Pill key={i} text={p} color={f.color} />)}</div>
      </div>
    </div>
  );
}

// ── Risk Spectrum Bar ──
function EquityRiskSpectrum() {
  const funds = [
    { label: "Money Market", score: 5, color: green },
    { label: "Bond Fund", score: 20, color: blue2 },
    { label: "Standard Equity", score: 45, color: blue2 },
    { label: "Equity Growth", score: 68, color: gold },
    { label: "Small Cap", score: 85, color: red },
    { label: "Specialty", score: 95, color: "#f97316" },
  ];
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 16 }}>📊 Mutual Fund Risk Spectrum</div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.65rem", color: muted, marginBottom: 6 }}>
        <span>← Lower Risk</span><span>Higher Risk →</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: `linear-gradient(90deg,${green},${blue2},${gold},${red},#f97316)`, marginBottom: 20, position: "relative" }}>
        {funds.map((f, i) => (
          <div key={i} style={{ position: "absolute", left: `${f.score}%`, top: "50%", transform: "translate(-50%,-50%)", width: 14, height: 14, borderRadius: "50%", background: f.color, border: `2px solid ${navy}` }} />
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))", gap: 6 }}>
        {funds.map((f, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: f.color, flexShrink: 0 }} />
            <span style={{ fontSize: "0.75rem", color: textCol }}>{f.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Market Cap Calculator ──
function MarketCapCalc() {
  const [companies, setCompanies] = useState([
    { name: "ABC Corp", price: 20, shares: 5000000 },
    { name: "XYZ Corp", price: 10, shares: 1000000 },
  ]);
  const [results, setResults] = useState(null);

  function update(i, field, val) {
    const updated = companies.map((c, idx) => idx === i ? { ...c, [field]: field === "name" ? val : parseFloat(val) || 0 } : c);
    setCompanies(updated);
    setResults(null);
  }

  function calc() {
    const res = companies.map(c => {
      const cap = c.price * c.shares;
      const category = cap >= 10e9 ? { label: "Large Cap", color: blue2 }
        : cap >= 2e9 ? { label: "Mid Cap", color: gold }
        : cap >= 300e6 ? { label: "Small Cap", color: purple }
        : { label: "Micro Cap", color: red };
      return { ...c, cap, category };
    });
    setResults(res);
  }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🧮 Market Capitalization Calculator</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.8rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "6px 14px", marginBottom: 20, display: "inline-block" }}>
        Market Cap = Share Price × Shares Outstanding
      </div>
      <div style={{ display: "grid", gap: 10, marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {["Company Name", "Share Price ($)", "Shares Outstanding"].map(lbl => (
            <div key={lbl} style={{ fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>{lbl}</div>
          ))}
        </div>
        {companies.map((c, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, alignItems: "center" }}>
            <input value={c.name} onChange={e => update(i, "name", e.target.value)}
              style={{ background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "inherit", fontSize: "0.85rem", outline: "none" }} />
            {["price", "shares"].map(field => (
              <input key={field} type="number" value={c[field]} onChange={e => update(i, field, e.target.value)}
                style={{ background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.85rem", outline: "none" }} />
            ))}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
        <button onClick={() => { setCompanies([...companies, { name: `Company ${companies.length + 1}`, price: 0, shares: 0 }]); setResults(null); }}
          style={{ background: "transparent", color: blue2, border: `1px solid rgba(59,130,246,.3)`, padding: "12px 20px", fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>+ Add Company</button>
      </div>
      {results && (
        <div style={{ marginTop: 16, display: "grid", gap: 8 }}>
          {results.map((r, i) => (
            <div key={i} style={{ background: navy4, border: `1px solid ${r.category.color}33`, borderRadius: 8, padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <div>
                <div style={{ fontSize: "0.88rem", fontWeight: 700, color: white }}>{r.name}</div>
                <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted, marginTop: 2 }}>${r.price.toLocaleString()} × {r.shares.toLocaleString()} shares</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "monospace", fontSize: "1.1rem", fontWeight: 700, color: r.category.color }}>${r.cap.toLocaleString()}</div>
                <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: 100, color: r.category.color, background: `${r.category.color}18`, border: `1px solid ${r.category.color}33` }}>{r.category.label}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Fund Return Calculator ──
function FundReturnCalc() {
  const [startNav, setStartNav] = useState(15.00);
  const [endNav, setEndNav] = useState(16.50);
  const [distribution, setDistribution] = useState(0.50);
  const [result, setResult] = useState(null);

  function calc() {
    const s = parseFloat(startNav), e = parseFloat(endNav), d = parseFloat(distribution);
    const gain = (e - s) + d;
    const ret = (gain / s) * 100;
    setResult({ s, e, d, gain: gain.toFixed(2), ret: ret.toFixed(2) });
  }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🧮 Fund Return (NAVPU Change)</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.8rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "6px 14px", marginBottom: 20, display: "inline-block", lineHeight: 1.8 }}>
        Return = [(End NAVPU − Start NAVPU + Distributions) ÷ Start NAVPU] × 100
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginBottom: 16 }}>
        {[["Start NAVPU ($)", startNav, setStartNav], ["End NAVPU ($)", endNav, setEndNav], ["Distributions ($)", distribution, setDistribution]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" step="0.01" value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "11px 13px", color: white, fontFamily: "monospace", fontSize: "0.9rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
      {result && (
        <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.2)", borderRadius: 8, padding: 20, marginTop: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            {[["Total Gain per Unit", `$${result.gain}`, blue2], ["Total Return", `${result.ret}%`, parseFloat(result.ret) >= 0 ? green : red]].map(([lbl, val, col]) => (
              <div key={lbl} style={{ background: navy3, borderRadius: 6, padding: "12px 14px", textAlign: "center" }}>
                <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, marginBottom: 4 }}>{lbl}</div>
                <div style={{ fontFamily: "monospace", fontSize: "1.4rem", fontWeight: 700, color: white }}>{val}</div>
              </div>
            ))}
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, borderTop: `1px solid ${border}`, paddingTop: 12 }}>
            {[
              `Capital gain: $${result.e} − $${result.s} = $${(result.e - result.s).toFixed(2)}`,
              `Add distribution: $${(result.e - result.s).toFixed(2)} + $${result.d} = $${result.gain} total gain per unit`,
              `Return: $${result.gain} ÷ $${result.s} × 100 = ${result.ret}%`,
            ].map((step, i) => (
              <div key={i} style={{ fontFamily: "monospace", fontSize: "0.79rem", color: textCol, padding: "4px 0", display: "flex", gap: 8 }}>
                <span style={{ color: gold, flexShrink: 0 }}>→</span>{step}
              </div>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── S2: Balanced Funds ──
function BalancedFundPanel() {
  const [outlook, setOutlook] = useState("bullish");
  const configs = {
    bullish: {
      label: "Bullish on Stocks", icon: "📈", color: green,
      equity: 70, bonds: 20, cash: 10,
      reason: "Manager expects equities to outperform. Shifts weighting toward stocks to capture upside. Reduced bond and cash allocation.",
    },
    bearish: {
      label: "Bearish / Rates Rising", icon: "📉", color: red,
      equity: 30, bonds: 25, cash: 45,
      reason: "Manager expects stock market weakness or rising rates. Shifts toward cash and short-term bonds to protect capital. Defensive positioning.",
    },
    neutral: {
      label: "Neutral Market View", icon: "⚖️", color: blue2,
      equity: 50, bonds: 35, cash: 15,
      reason: "Balanced baseline allocation. Roughly equal weighting between growth (equities) and safety (fixed income + cash). Standard strategic mix.",
    },
  };
  const cfg = configs[outlook];
  const segments = [
    { label: "Equities", pct: cfg.equity, color: gold },
    { label: "Bonds", pct: cfg.bonds, color: blue2 },
    { label: "Cash", pct: cfg.cash, color: green },
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 26 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 18 }}>⚖️ Balanced Fund — Market Timing Simulator</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 24 }}>
          {Object.entries(configs).map(([key, val]) => (
            <button key={key} onClick={() => setOutlook(key)} style={{ flex: 1, minWidth: 100, padding: "10px 14px", background: outlook === key ? navy4 : navy3, border: `1px solid ${outlook === key ? val.color + "55" : border}`, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: outlook === key ? val.color : muted, transition: "all 0.2s" }}>{val.icon} {val.label}</button>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, alignItems: "center" }}>
          <div>
            <div style={{ height: 28, borderRadius: 6, overflow: "hidden", display: "flex", marginBottom: 12 }}>
              {segments.map((seg, i) => (
                <div key={i} style={{ width: `${seg.pct}%`, background: seg.color, transition: "width 0.5s ease", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {seg.pct >= 15 && <span style={{ fontSize: "0.7rem", fontWeight: 700, color: navy }}>{seg.pct}%</span>}
                </div>
              ))}
            </div>
            {segments.map((seg, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: seg.color, flexShrink: 0 }} />
                <span style={{ fontSize: "0.82rem", color: textCol }}>{seg.label}</span>
                <span style={{ fontFamily: "monospace", fontSize: "0.82rem", color: seg.color, marginLeft: "auto" }}>{seg.pct}%</span>
              </div>
            ))}
          </div>
          <div style={{ background: navy4, borderLeft: `3px solid ${cfg.color}`, borderRadius: 6, padding: "14px 18px" }}>
            <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: cfg.color, marginBottom: 8 }}>Manager Rationale</div>
            <div style={{ fontSize: "0.85rem", color: textCol, lineHeight: 1.7 }}>{cfg.reason}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Target Date Glide Path ──
function GlidePath() {
  const [yearsOut, setYearsOut] = useState(15);

  function getAlloc(y) {
    if (y >= 15) return { equity: 85, bond: 12, cash: 3 };
    if (y >= 10) return { equity: 70, bond: 25, cash: 5 };
    if (y >= 7) return { equity: 55, bond: 35, cash: 10 };
    if (y >= 5) return { equity: 40, bond: 45, cash: 15 };
    if (y >= 3) return { equity: 20, bond: 50, cash: 30 };
    if (y >= 1) return { equity: 5, bond: 30, cash: 65 };
    return { equity: 0, bond: 0, cash: 100 };
  }

  const alloc = getAlloc(yearsOut);
  const phase = yearsOut >= 10 ? { label: "Growth Phase", color: gold, desc: "Heavy equity weighting — maximizing long-term capital growth. Risk is acceptable because there's time to recover from downturns." }
    : yearsOut >= 5 ? { label: "Transition Phase", color: blue2, desc: "Gradually shifting from equities to fixed income. Locking in gains and reducing volatility as the target date approaches." }
    : yearsOut >= 1 ? { label: "Preservation Phase", color: green, desc: "Mostly cash and short-term bonds. Capital preservation is now the priority. Almost no market risk." }
    : { label: "Maturity — Fully Defensive", color: green, desc: "100% cash equivalent. The fund is ready for withdrawal — full capital preservation." };

  const segments = [
    { label: "Equities", pct: alloc.equity, color: gold },
    { label: "Fixed Income", pct: alloc.bond, color: blue2 },
    { label: "Cash", pct: alloc.cash, color: green },
  ];

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🎯 Target-Date Fund Glide Path</div>
      <p style={{ fontSize: "0.85rem", color: muted, marginBottom: 20, lineHeight: 1.65 }}>
        Drag the slider to see how the fund's asset allocation automatically shifts as the target date approaches — from aggressive growth to full capital preservation.
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
        <label style={{ fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", flexShrink: 0 }}>Years to Target</label>
        <input type="range" min={0} max={20} step={1} value={yearsOut} onChange={e => setYearsOut(parseInt(e.target.value))} style={{ flex: 1, accentColor: gold }} />
        <span style={{ fontFamily: "monospace", fontSize: "1rem", color: gold, width: 60, textAlign: "right" }}>{yearsOut} yrs</span>
      </div>
      {/* Mini timeline */}
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.62rem", color: muted, marginBottom: 6 }}>
        <span>0 yrs (maturity)</span><span>20 yrs (early)</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: `linear-gradient(90deg,${green},${blue2},${gold})`, marginBottom: 24 }} />
      {/* Allocation bar */}
      <div style={{ height: 32, borderRadius: 6, overflow: "hidden", display: "flex", marginBottom: 14 }}>
        {segments.map((seg, i) => (
          <div key={i} style={{ width: `${seg.pct}%`, background: seg.color, transition: "width 0.5s ease", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {seg.pct >= 12 && <span style={{ fontSize: "0.72rem", fontWeight: 700, color: navy }}>{seg.pct}%</span>}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
        {segments.map((seg, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: seg.color }} />
            <span style={{ fontSize: "0.78rem", color: muted }}>{seg.label} <strong style={{ color: seg.color }}>{seg.pct}%</strong></span>
          </div>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${phase.color}44`, borderRadius: 8, padding: "14px 18px" }}>
        <div style={{ fontSize: "0.75rem", fontWeight: 700, color: phase.color, marginBottom: 4 }}>{phase.label}</div>
        <div style={{ fontSize: "0.84rem", color: textCol, lineHeight: 1.7 }}>{phase.desc}</div>
      </div>
      <div style={{ marginTop: 14, background: navy4, borderRadius: 8, padding: "14px 18px", fontSize: "0.82rem", color: muted, lineHeight: 1.7 }}>
        💡 <strong style={{ color: textCol }}>Emma's Education Fund (Case Study):</strong> Ryan recommends the Crystal Target 2035 Fund for Emma, now age 2. With 15 years until university, the fund starts aggressive (85% equity) and automatically glides to 100% cash by 2035 — no rebalancing needed by Carol and Kevin.
      </div>
    </div>
  );
}

// ── S3: Global Funds ──
function GlobalFundPanel() {
  const [cadDir, setCadDir] = useState("rises");
  const configs = {
    rises: {
      label: "CAD Rises ↑", color: red,
      impact: "↓ Foreign asset value falls",
      impactColor: red,
      reason: "When the Canadian dollar strengthens, foreign investments (priced in USD, EUR, etc.) are worth fewer CAD when converted back. An unhedged global fund will see its NAV decline.",
      example: "You own a US stock worth $100 USD. At 1 CAD = 0.75 USD it's worth $133 CAD. When CAD rises to 1:1, it's worth only $100 CAD — a $33 CAD loss with no change in the stock price.",
    },
    falls: {
      label: "CAD Falls ↓", color: green,
      impact: "↑ Foreign asset value rises",
      impactColor: green,
      reason: "A weaker Canadian dollar means foreign holdings are worth more CAD on conversion. Global funds provide a natural hedge if the CAD weakens — this is sometimes called a 'currency hedge'.",
      example: "You own a US stock worth $100 USD. At 1:1 it's worth $100 CAD. When CAD falls to 1 CAD = 0.75 USD, the stock is now worth $133 CAD — a $33 gain with no change in the stock price.",
    },
  };
  const cfg = configs[cadDir];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 26 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 16 }}>💱 Currency Risk Simulator</div>
        <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
          {Object.entries(configs).map(([key, val]) => (
            <button key={key} onClick={() => setCadDir(key)} style={{ flex: 1, padding: "10px 16px", background: cadDir === key ? navy4 : navy3, border: `1px solid ${cadDir === key ? val.color + "55" : border}`, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: cadDir === key ? val.color : muted, transition: "all 0.2s" }}>{val.label}</button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 16 }}>
          <div style={{ background: navy4, borderRadius: 8, padding: "16px 20px", textAlign: "center", minWidth: 130 }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, marginBottom: 6 }}>Canadian Dollar</div>
            <div style={{ fontFamily: "monospace", fontSize: "1.4rem", fontWeight: 700, color: cadDir === "rises" ? green : red }}>{cfg.label}</div>
          </div>
          <div style={{ fontSize: "1.5rem", color: muted }}>→</div>
          <div style={{ background: navy4, borderRadius: 8, padding: "16px 20px", textAlign: "center", minWidth: 130 }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, marginBottom: 6 }}>Global Fund NAV</div>
            <div style={{ fontFamily: "monospace", fontSize: "1.2rem", fontWeight: 700, color: cfg.impactColor }}>{cfg.impact}</div>
          </div>
        </div>
        <div style={{ background: navy4, borderLeft: `3px solid ${cfg.color}`, borderRadius: 6, padding: "13px 18px", marginBottom: 10 }}>
          <div style={{ fontSize: "0.84rem", color: textCol, lineHeight: 1.7 }}>{cfg.reason}</div>
        </div>
        <div style={{ background: `${cfg.color}0d`, border: `1px solid ${cfg.color}22`, borderRadius: 6, padding: "12px 16px", fontSize: "0.8rem", color: muted, lineHeight: 1.7 }}>
          📌 Example: {cfg.example}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 10, marginTop: 14 }}>
        {[
          { icon: "🔄", title: "Diversification Benefit", body: "Different countries have different economic cycles. When Canada is in recession, Europe or Asia may be thriving — reducing overall portfolio volatility.", color: blue2 },
          { icon: "ℹ️", title: "Information Risk", body: "Accounting standards and transparency vary by country. Emerging markets may have less disclosure, making analysis harder.", color: gold },
          { icon: "💧", title: "Liquidity Risk", body: "Emerging market exchanges have lower trading volumes — it may be harder to buy or sell at fair prices quickly.", color: purple },
          { icon: "🌍 vs 🇨🇦", title: "Global vs. International", body: "'Global funds' include Canada. 'International funds' exclude Canada — they hold only foreign markets.", color: green },
        ].map((card, i) => (
          <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 16px" }}>
            <div style={{ fontSize: "1.1rem", marginBottom: 8 }}>{card.icon}</div>
            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: white, marginBottom: 4 }}>{card.title}</div>
            <div style={{ fontSize: "0.78rem", color: muted, lineHeight: 1.65 }}>{card.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── S4: Specialty Funds ──
function SpecialtyFunds() {
  const [active, setActive] = useState(null);
  const funds = [
    { icon: "🥇", name: "Precious Metals", color: gold, sectors: ["Gold", "Silver", "Mining companies"], risk: "Highly volatile — correlates with commodity cycles and global uncertainty." },
    { icon: "🛢️", name: "Natural Resources", color: "#f97316", sectors: ["Oil & Gas", "Forestry", "Mining"], risk: "Tied to commodity prices — highly cyclical and sensitive to global demand." },
    { icon: "🏢", name: "Real Estate", color: blue2, sectors: ["REITs", "Commercial property", "Residential"], risk: "Interest rate sensitive — rising rates increase borrowing costs and reduce valuations." },
    { icon: "💻", name: "Science & Technology", color: purple, sectors: ["Tech firms", "Biotech", "AI companies"], risk: "High P/E valuations; disruptive competition; regulatory risk." },
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10, marginBottom: 14 }}>
        {funds.map((f, i) => (
          <div key={i} onClick={() => setActive(active === i ? null : i)}
            style={{ background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? f.color + "55" : border}`, borderRadius: 8, padding: "18px 14px", cursor: "pointer", transition: "all 0.2s", textAlign: "center", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: f.color }} />
            <div style={{ fontSize: "1.6rem", marginBottom: 8 }}>{f.icon}</div>
            <div style={{ fontSize: "0.82rem", fontWeight: 700, color: white }}>{f.name}</div>
            <div style={{ fontSize: "0.7rem", color: f.color, marginTop: 2 }}>Specialty Fund</div>
          </div>
        ))}
      </div>
      {active !== null && (
        <div style={{ background: navy4, border: `1px solid ${funds[active].color}44`, borderRadius: 8, padding: 22 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
            <span style={{ fontSize: "1.6rem" }}>{funds[active].icon}</span>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white }}>{funds[active].name} Fund</div>
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            <div style={{ background: navy3, borderRadius: 6, padding: "10px 14px", display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: funds[active].color, width: 80, flexShrink: 0 }}>Sectors</div>
              <div style={{ fontSize: "0.84rem", color: textCol, flex: 1 }}>{funds[active].sectors.join(" · ")}</div>
            </div>
            <div style={{ background: navy3, borderRadius: 6, padding: "10px 14px", display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: red, width: 80, flexShrink: 0 }}>Key Risk</div>
              <div style={{ fontSize: "0.84rem", color: textCol, flex: 1 }}>{funds[active].risk}</div>
            </div>
          </div>
        </div>
      )}
      <Callout icon="🎯" accent={red}>
        <strong style={{ color: red }}>Concentration Risk:</strong> Specialty funds invest in a single sector or region — the opposite of diversification. If that sector crashes (e.g., oil prices collapse), the entire fund suffers. These should be a small <strong>"satellite"</strong> holding at the edges of a portfolio — never the core.
      </Callout>
    </div>
  );
}

// ── Case Study ──
function CaseStudy() {
  return (
    <div style={{ background: navy3, border: `1px solid ${gold}33`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", color: gold, fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "4px 12px", borderRadius: 100, marginBottom: 16 }}>
        📖 Case Study
      </div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem", color: white, marginBottom: 14 }}>Saving for Emma's Education — Target 2035 Fund</div>
      <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
        {[
          { label: "Scenario", val: "Carol & Kevin have a 2-year-old daughter Emma. They want to save for university (15 years away). High risk tolerance, but want a hands-off solution.", color: blue2 },
          { label: "Recommendation", val: "Ryan recommends the Crystal Target Education 2035 Fund — a target-date fund with a maturity date matching Emma's first year of university.", color: gold },
          { label: "Why It Works", val: "The fund starts aggressive (85% equity) to maximize 15-year growth, then automatically glides to bonds, then 100% cash by 2035 — no monitoring needed.", color: green },
          { label: "Key Benefit", val: "Carol and Kevin don't need to worry about rebalancing, market timing, or switching funds as Emma gets older. The glide path does it automatically.", color: purple },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ background: navy4, borderLeft: `3px solid ${color}`, borderRadius: 6, padding: "12px 16px", display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color, width: 110, flexShrink: 0, paddingTop: 2 }}>{label}</div>
            <div style={{ fontSize: "0.85rem", color: textCol, flex: 1, lineHeight: 1.65 }}>{val}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {[["Age 2 (now)", "85% Equity", gold], ["Age 10", "55% Equity", blue2], ["Age 15", "20% Equity", green], ["Age 17 (2035)", "100% Cash", green]].map(([age, alloc, col], i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 0 }}>
            <div style={{ background: navy4, border: `1px solid ${col}44`, borderRadius: 6, padding: "8px 12px", textAlign: "center" }}>
              <div style={{ fontSize: "0.65rem", color: muted }}>{age}</div>
              <div style={{ fontSize: "0.75rem", fontWeight: 700, color: col }}>{alloc}</div>
            </div>
            {i < 3 && <div style={{ width: 20, height: 2, background: `${border}`, margin: "0 2px" }}>→</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Quiz ──
const questions = [
  { q: "Which type of equity fund uses a 'Passive' investment strategy?", opts: ["Equity Growth Fund", "Equity Index Fund", "Small Cap Fund", "Precious Metals Fund"], correct: 1, fb: { y: "Correct! Index funds replicate a market index using passive management — no active stock picking, no research costs, resulting in lower MERs.", n: "Not quite. Equity Index funds are passive — they simply hold stocks in the same proportion as a market index. Active management applies to Growth, Small Cap, and Specialty funds." } },
  { q: "What is the primary risk of a Specialty Mutual Fund?", opts: ["It holds too much cash", "It is too diversified", "Concentration risk — lack of diversification", "Interest rate risk"], correct: 2, fb: { y: "Correct! Specialty funds concentrate in one sector or region. If that sector crashes, the entire fund suffers — the opposite of the diversification benefit.", n: "Not quite. Specialty funds have concentration risk — putting all eggs in one basket. If that single sector underperforms, there's no other holding to offset losses." } },
  { q: "A Target-Date Fund is best described as:", opts: ["A fund that aims to beat the TSX by a specific date", "A fund with a static asset allocation that never changes", "A fund that adjusts its mix from aggressive to conservative as a maturity date approaches", "A short-term money market fund"], correct: 2, fb: { y: "Correct! Target-date funds use a glide path — starting equity-heavy for growth and automatically shifting to bonds and cash as the target date approaches.", n: "Not quite. Target-date funds have a dynamic allocation — the 'glide path' systematically shifts from equities (early) to cash/bonds (near maturity) automatically." } },
  { q: "If the Canadian dollar RISES relative to the US dollar, what happens to a US Equity Fund held by a Canadian investor (unhedged)?", opts: ["The value increases", "The value decreases", "It has no impact", "The dividend yield doubles"], correct: 1, fb: { y: "Correct! A stronger CAD means US assets convert to fewer CAD. The foreign investment loses value in Canadian dollar terms even if the underlying stock didn't change.", n: "Not quite. A rising CAD reduces the CAD value of foreign holdings. A US asset worth $100 USD is worth fewer Canadian dollars when the CAD is strong." } },
  { q: "Which type of fund would most likely hold shares of small, start-up companies with no dividend history?", opts: ["Preferred Dividend Fund", "Standard Equity Fund", "Equity Growth Fund", "Money Market Fund"], correct: 2, fb: { y: "Correct! Equity Growth funds invest in smaller, fast-growing companies that reinvest earnings rather than paying dividends. Capital gains, not income, is the goal.", n: "Not quite. Growth funds target companies that reinvest earnings to grow — no dividends, high P/E, high potential. Preferred Dividend funds need dividend-paying companies." } },
  { q: "'Blue Chip' companies are typically found in which type of fund?", opts: ["Small Cap Fund", "Standard Equity Fund (Conservative)", "Venture Capital Fund", "Emerging Markets Fund"], correct: 1, fb: { y: "Correct! Standard (conservative) equity funds focus on large-cap, established 'Blue Chip' companies with strong dividend records and long operating histories.", n: "Not quite. Blue Chip = large, stable, dividend-paying companies. These fit the Standard Equity (conservative) fund. Small Cap and venture funds hold the opposite." } },
  { q: "What does 'Glide Path' refer to in a mutual fund context?", opts: ["The flight path of the fund manager", "The gradual change in asset allocation in a target-date fund over time", "The projected growth rate of an equity fund", "The declining management fee of a fund"], correct: 1, fb: { y: "Correct! The glide path is the automatic, time-driven shift from aggressive (equity-heavy) to conservative (cash/bond-heavy) allocations in a target-date fund.", n: "Not quite. The glide path describes how a target-date fund's asset allocation changes over time — more equities early, more bonds/cash near maturity." } },
  { q: "Global Mutual Funds primarily benefit investors by:", opts: ["Avoiding all taxes", "Benefiting from economic cycles in different countries", "Eliminating all market risk", "Guaranteeing their principal"], correct: 1, fb: { y: "Correct! Global diversification exploits the fact that different countries' economic cycles are not perfectly correlated. One region's downturn may be offset by another's growth.", n: "Not quite. The main benefit is diversification across economic cycles. Global funds don't eliminate risk or guarantee returns — they spread exposure across markets." } },
  { q: "A balanced fund manager expects the stock market to crash. What action might they take?", opts: ["Shift weighting toward bonds and cash", "Shift weighting toward more equities", "Buy more small-cap stocks", "Increase the duration of the equity portfolio"], correct: 0, fb: { y: "Correct! Defensive positioning — reducing equity exposure and increasing bonds and cash — protects capital during an expected market downturn.", n: "Not quite. A bearish outlook calls for reducing equity exposure and moving toward bonds and cash — a defensive, capital-preserving stance." } },
  { q: "Small Cap funds are generally considered:", opts: ["Less risky than Blue Chip funds", "More volatile and risky than Standard Equity funds", "Suitable for short-term investors", "Immune to market downturns"], correct: 1, fb: { y: "Correct! Small cap companies have higher failure rates, less liquidity, and often trade at high valuations. They are more volatile than large, established Blue Chip companies.", n: "Not quite. Small cap = small companies with higher volatility, higher failure rates, and greater price swings. They are more risky than standard equity funds." } },
];

function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answered).filter(a => a.correct).length;
  const total = questions.length;
  function answer(idx) { if (answered[current] !== undefined) return; setAnswered({ ...answered, [current]: { chosen: idx, correct: idx === questions[current].correct } }); }
  function next() { if (current < total - 1) setCurrent(current + 1); else setDone(true); }
  function retry() { setCurrent(0); setAnswered({}); setDone(false); }
  const q = questions[current], ans = answered[current];
  const pct = Math.round(score / total * 100);

  return (
    <div style={{ background: navy2, border: `1px solid ${border}`, borderRadius: 12, padding: 36, marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.3rem", color: white }}>Chapter 12 Quiz</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>Question {current + 1} of {total}</div>
      </div>
      <div style={{ height: 4, background: navy4, borderRadius: 2, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${gold},${blue})`, borderRadius: 2, width: `${(current + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
      </div>
      {!done ? (
        <>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.05rem", color: white, lineHeight: 1.55, marginBottom: 22 }}>{q.q}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {q.opts.map((opt, i) => {
              const isChosen = ans?.chosen === i, isCorrect = i === q.correct;
              let bg = navy3, bdr = border, col = textCol;
              if (ans) { if (isCorrect) { bg = "rgba(34,197,94,.08)"; bdr = green; col = "#4ade80"; } else if (isChosen) { bg = "rgba(239,68,68,.08)"; bdr = "#ef4444"; col = "#f87171"; } }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 8, padding: "14px 18px", textAlign: "left", fontFamily: "inherit", fontSize: "0.88rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 26, height: 26, flexShrink: 0, background: navy4, border: `1px solid ${border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: muted }}>{["A","B","C","D"][i]}</span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "13px 16px", borderRadius: 7, fontSize: "0.84rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.2)"}`, color: ans.correct ? "#86efac" : "#fca5a5" }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ marginTop: 20, background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>
                {current < total - 1 ? "Next Question →" : "See My Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", margin: "0 auto 20px", background: `conic-gradient(${gold} 0% ${pct}%, ${navy4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", inset: 14, borderRadius: "50%", background: navy2 }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.5rem", color: white, marginBottom: 8 }}>
            {score === total ? "Perfect Score! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
            {score === total ? "Flawless. You've mastered equity and specialty fund types." : score >= 8 ? "Excellent — review any missed questions." : score >= 6 ? "Good start. Revisit glide paths, currency risk, and fund categories." : "Focus on the five equity fund types, target-date funds, and global currency effects."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: gold, border: `1px solid rgba(245,166,35,.35)`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// ── Glossary ──
const glossary = [
  { term: "Standard Equity Fund", def: "Invests in large-cap 'Blue Chip' stocks. Objective: dividend income + capital gains. Lower risk than growth funds." },
  { term: "Equity Growth Fund", def: "Invests in smaller, fast-growing companies. Objective: capital gains only. Higher risk, high P/E valuations." },
  { term: "Equity Index Fund", def: "Passive fund replicating a market index (e.g., S&P/TSX). Low MER; no manager risk; accepts market returns." },
  { term: "Small Cap Fund", def: "Subset of growth funds. Invests in small capitalization companies. Highest volatility; higher failure rate." },
  { term: "Responsible Investment (ESG)", def: "Incorporates Environmental, Social & Governance factors. Screens out companies with poor ESG practices." },
  { term: "Blue Chip", def: "Large, established companies with strong dividend records and long operating histories. Held in conservative equity funds." },
  { term: "Market Capitalization", def: "Share Price × Shares Outstanding. Determines if a company is Small Cap, Mid Cap, or Large Cap." },
  { term: "Balanced Fund", def: "Hybrid fund holding both equities and bonds. Objective: mix of safety, income, and growth. Manager adjusts mix based on market outlook." },
  { term: "Market Timing", def: "A balanced fund manager's strategy to shift allocations based on market expectations — more equities in bull markets, more bonds/cash in bear markets." },
  { term: "Target-Date Fund", def: "A balanced fund with a specific maturity date. Asset mix automatically becomes more conservative as the target date approaches." },
  { term: "Glide Path", def: "The automatic, time-driven shift in a target-date fund from aggressive (equity-heavy) to conservative (cash/bond-heavy) as the target date nears." },
  { term: "Life-Cycle Fund", def: "Another name for a target-date fund. Manages the growth-to-preservation transition over an investor's life cycle." },
  { term: "Global Fund", def: "Invests in securities from multiple countries, including Canada. Provides economic cycle diversification." },
  { term: "International Fund", def: "Like a global fund but excludes Canadian securities — foreign markets only." },
  { term: "Currency Risk (FX Risk)", def: "If the CAD rises, foreign assets are worth fewer CAD when converted. If CAD falls, foreign assets gain value in CAD terms." },
  { term: "Information Risk", def: "Risk from lower accounting standards and transparency in some foreign markets, making analysis less reliable." },
  { term: "Specialty Fund", def: "Concentrates in a single sector (e.g., precious metals, tech, real estate). High concentration risk." },
  { term: "Concentration Risk", def: "The risk of losses due to overexposure to a single sector, company, or region. The primary risk of specialty funds." },
  { term: "Fund Return Formula", def: "[(End NAVPU − Start NAVPU + Distributions) ÷ Start NAVPU] × 100. Captures both price change and income distributed." },
];

// ── Main Export ──
export default function Chapter12() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const fn = () => {
      const h = document.documentElement;
      setProgress((h.scrollTop || document.body.scrollTop) / (h.scrollHeight - h.clientHeight) * 100);
    };
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const tocItems = [
    ["01", "Equity Fund Types — Standard, Growth, Index, Small Cap, ESG"],
    ["02", "Balanced Funds & Market Timing"],
    ["03", "Target-Date Funds & the Glide Path"],
    ["04", "Global Mutual Funds & Currency Risk"],
    ["05", "Specialty Funds & Concentration Risk"],
    ["📖", "Case Study — Emma's Education Fund"],
    ["🧮", "Market Cap & Fund Return Calculators"],
    ["✦", "Knowledge Check Quiz"],
    ["✦", "Key Terms Glossary"],
  ];

  return (
    <div style={{ background: navy, minHeight: "100vh", fontFamily: "'Nunito Sans', sans-serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, height: 3, background: `linear-gradient(90deg,${gold},${blue})`, width: `${progress}%`, zIndex: 9999, transition: "width 0.15s ease" }} />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 80px" }}>

        <Reveal>
          <div style={{ padding: "80px 0 56px", borderBottom: `1px solid ${border}`, marginBottom: 56 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.25)", color: gold, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "5px 14px", borderRadius: 100, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: gold, display: "inline-block" }} />
              IFC/CSC · Chapter 12
            </div>
            <h1 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(2.2rem,5vw,3.4rem)", fontWeight: 900, color: white, lineHeight: 1.15, marginBottom: 20 }}>
              Equity, Balanced &amp; <span style={{ color: gold }}>Specialty Funds</span>
            </h1>
            <p style={{ fontSize: "1.05rem", color: muted, maxWidth: 600, lineHeight: 1.8, marginBottom: 32 }}>
              Five equity fund types, balanced fund market timing, target-date glide paths, global currency risk, and specialty fund concentration risk — with interactive simulators and two calculators.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, paddingTop: 24, borderTop: `1px solid ${border}` }}>
              {[["📖","5","Core Topics"],["⏱","~18 min","read"],["🎯","10","practice questions"],["🧮","2","calculators"],["📊","3","interactive simulators"]].map(([icon,val,lbl],i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: blue }}>{icon}</span>
                  <span style={{ fontSize: "0.82rem", color: muted }}><strong style={{ color: textCol }}>{val}</strong> {lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${gold}`, borderRadius: 8, padding: "28px 32px", marginBottom: 56 }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "0.85rem", fontWeight: 700, color: gold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>📋 In This Chapter</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {tocItems.map(([num, label], i) => (
                <li key={i} style={{ padding: "6px 0", borderBottom: i < tocItems.length - 1 ? `1px solid ${border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontFamily: "monospace", fontSize: "0.7rem", color: muted, width: 24, flexShrink: 0 }}>{num}</span>
                  <span style={{ color: textCol, fontSize: "0.9rem", fontWeight: 600 }}>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* S1 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 01</span><span style={ss.badge}>Equity Funds</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Equity Mutual Funds</h2>
            <p style={ss.prose}>
              Equity funds invest in the common and preferred shares of public companies. They are the <span style={{ color: gold, fontWeight: 600 }}>riskiest of the three basic fund types</span> — but offer the highest long-term growth potential. There are five distinct types, each with a different objective and risk profile:
            </p>
          </Reveal>
          <Reveal delay={0.1}><EquityFundTabs /></Reveal>
          <Reveal delay={0.2}><EquityRiskSpectrum /></Reveal>
          <Reveal delay={0.3}>
            <Callout icon="🌱">
              <strong>Responsible Investment (RI / ESG):</strong> ESG funds screen companies on Environmental (pollution, carbon), Social (labour practices, community), and Governance (board independence, transparency) criteria. The goal isn't just ethics — it's also <strong>risk management</strong>. Companies with poor ESG practices may face regulatory action, reputational damage, or operational disruption.
            </Callout>
          </Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S2 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 02</span><span style={ss.badge}>Balanced Funds</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Balanced Mutual Funds</h2>
            <p style={ss.prose}>
              Balanced funds are <span style={{ color: gold, fontWeight: 600 }}>hybrid funds</span> holding both equities and fixed income. The manager actively adjusts the mix based on their market outlook — shifting toward stocks when bullish and toward cash/bonds when defensive. Performance falls between bond funds and equity funds:
            </p>
          </Reveal>
          <Reveal delay={0.1}><BalancedFundPanel /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="💡" accent={blue}>
              <strong style={{ color: blue2 }}>Balanced vs. Equity:</strong> A balanced fund is less volatile than a pure equity fund — the bond and cash component cushions downturns. However, it also participates less in bull markets. It suits investors who want growth with a built-in safety buffer.
            </Callout>
          </Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S3 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 03</span><span style={ss.badge}>Target-Date Funds</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Target-Date Funds &amp; the Glide Path</h2>
            <p style={ss.prose}>
              A target-date (life-cycle) fund is a balanced fund with a <span style={{ color: gold, fontWeight: 600 }}>specific future maturity date</span>. Its asset allocation automatically becomes more conservative as that date approaches — via the "glide path." Drag the slider below to see how Emma's 2035 Education Fund shifts over time:
            </p>
          </Reveal>
          <Reveal delay={0.1}><GlidePath /></Reveal>
          <Reveal delay={0.2}><CaseStudy /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S4 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 04</span><span style={ss.badge}>Global Funds</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Global Mutual Funds</h2>
            <p style={ss.prose}>
              Global funds hold assets across multiple countries. The key benefit is <span style={{ color: gold, fontWeight: 600 }}>geographic diversification</span> — different economies move at different speeds. The key risk is <span style={{ color: red, fontWeight: 600 }}>currency risk</span> — use the simulator to see how a rising or falling CAD impacts a foreign holding:
            </p>
          </Reveal>
          <Reveal delay={0.1}><GlobalFundPanel /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S5 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 05</span><span style={ss.badge}>Specialty Funds</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Specialty Mutual Funds</h2>
            <p style={ss.prose}>
              Specialty funds concentrate in a <span style={{ color: gold, fontWeight: 600 }}>single sector, industry, or region</span>. They are the antithesis of diversification — and should be used as small "satellite" positions, never a portfolio's core:
            </p>
          </Reveal>
          <Reveal delay={0.1}><SpecialtyFunds /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* Calculators */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Calculators</span><span style={ss.badge}>Formulas & Practice</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Market Cap &amp; Fund Return</h2>
            <p style={ss.prose}>Two key Chapter 12 calculations — Market Capitalization (used to classify fund holdings by size) and Fund Return (NAVPU change + distributions):</p>
          </Reveal>
          <Reveal delay={0.1}><MarketCapCalc /></Reveal>
          <Reveal delay={0.2}><FundReturnCalc /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* Quiz */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Knowledge Check</span><span style={ss.badge}>10 Questions</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Test Your Understanding</h2>
            <p style={ss.prose}>10 questions covering equity fund types, balanced and target-date funds, global currency effects, and specialty fund risks from Chapter 12.</p>
          </Reveal>
          <Reveal delay={0.1}><Quiz /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* Glossary */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Quick Reference</span><span style={ss.badge}>Key Terms</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Glossary</h2>
            <p style={ss.prose}>All essential terms from Chapter 12 — equity fund types, balanced funds, global funds, and specialty funds.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10, margin: "28px 0" }}>
              {glossary.map((g, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 20px", transition: "border-color 0.2s", cursor: "default" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(245,166,35,.3)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = border}>
                  <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{g.term}</div>
                  <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{g.def}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

      </div>
    </div>
  );
}
